import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import * as Sentry from '@sentry/browser'
import { isEmpty } from 'lodash'
import queryString from 'query-string'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useLocation, useNavigate } from 'react-router-dom-v5-compat'
import { OnboardingProgressProvider } from 'shared/components/Context/OnboardingProgressContext'
import { DrawerProvider } from 'shared/components/Drawer/DrawerContext'
import { Spinner } from 'shared/components/Spinner'
import { loadAzureMapsApis } from 'shared/helpers/loadAzureMapApis'

import { Layout } from 'components/Layout/Layout'
import { AppContainer } from './AppContainer'
import { AppUserProvider } from './components/Context/AppUserProvider'
import { LayoutProvider } from './components/Context/LayoutContext'
import { REFERRAL_CODE, REFERRAL_SOURCE, REGISTER_INTENDED_PRODUCT_KEY } from './helpers/constants'
import { initAnalytics } from './helpers/initAnalytics'
import { useOnboardingChecklist } from './hooks/query/useOnboardingChecklist'
import { LOG_IN_REDIRECT } from './pages/Registration/VerifyCode/VerifyCode'
import { getSettings, setAzureMapsApiLoaded } from './redux/actions/settings/actions'
import { getUserAndTeammateInfo } from './redux/actions/users/actions'
import { currentTeamSelector } from './redux/selectors/team'
import { currentUserSelector, isVendorUserSelector } from './redux/selectors/users'
import { ApplicationState, ReduxDispatch } from './redux/store/models'
import { JOBS_PATH, REGISTRATION_PATH } from './Routes'

const initAnalyticsCalls = new Set<string>()

export const App: React.FC = ({ children }: { children?: React.ReactNode }) => {
  const dispatch: ReduxDispatch = useDispatch()
  const navigate = useNavigate()
  const settings = useSelector((state: ApplicationState) => state.settings)
  const isUserDataRefreshed = useRef(false)

  //  flag for nav, bookmarks and job list changes
  const isAuthenticated = useSelector((state: ApplicationState) => state.auth.isAuthenticated)
  const azureMapsApiKey = useSelector(
    (state: ApplicationState) => state.settings.azure_maps_api_key as string
  )
  const isVendorUser = useSelector(isVendorUserSelector)
  const currentUser = useSelector(currentUserSelector)
  const currentTeam = useSelector(currentTeamSelector)
  const location = useLocation()
  const isPublicPath =
    location.pathname.includes('registration') ||
    location.pathname.includes('mobile-auth') ||
    location.pathname.includes('job-tracking')
  const params = queryString.parse(location.search)
  const referralCode = params.referral_code as string
  const referralSource = params.referral_source as string
  const shouldFetchChecklist =
    isAuthenticated &&
    !isPublicPath &&
    Boolean(currentTeam?.product_subscription?.product_name) &&
    Boolean(currentUser.id) &&
    !isVendorUser

  const { allChecklistItems, isLoading: isChecklistLoading } = useOnboardingChecklist(currentUser.id, {
    enabled: shouldFetchChecklist,
  })

  if (referralCode || referralSource) {
    // save any referral info
    localStorage?.setItem?.(REFERRAL_CODE, referralCode)
    localStorage?.setItem?.(REFERRAL_SOURCE, referralSource)
  }

  useLayoutEffect(() => {
    if (isAuthenticated && !isPublicPath && (!isUserDataRefreshed.current || !currentUser.id)) {
      isUserDataRefreshed.current = true
      dispatch(getUserAndTeammateInfo()).then((data) => {
        if (data.vendor_membership) return

        if (!initAnalyticsCalls.has(data?.id)) {
          initAnalytics(data, data.team_object)
          initAnalyticsCalls.add(data?.id)
        }

        if (!data.team_object?.id) {
          const intendedProduct = localStorage?.getItem?.(REGISTER_INTENDED_PRODUCT_KEY)
          const redirectParam = (queryString.parse(window.location.search).redirectUrl as string) || ''

          if (redirectParam.startsWith('/registration')) {
            localStorage?.removeItem?.(REGISTER_INTENDED_PRODUCT_KEY)
            navigate(redirectParam)
          }

          navigate(
            intendedProduct ? generatePath(REGISTRATION_PATH, { bundle: intendedProduct }) : '/registration'
          )
        }
      })
    }
  }, [isAuthenticated, isPublicPath, dispatch, currentUser.id, navigate])

  useEffect(() => {
    if (currentUser?.id) {
      Sentry.configureScope((scope) => {
        scope.setUser({
          id: currentUser.id,
          team_id: currentTeam?.id,
          is_team_admin: currentUser.is_team_admin,
          is_team_driver: currentUser.is_team_driver,
        })
      })

      const redirectParam = (queryString.parse(window.location.search).redirectUrl as string) || ''
      const noRedirect = !redirectParam || redirectParam === '/'
      if (isAuthenticated && noRedirect && localStorage.getItem(LOG_IN_REDIRECT) === '1') {
        navigate(JOBS_PATH)
      }
      localStorage.removeItem(LOG_IN_REDIRECT)
    }
  }, [
    isAuthenticated,
    currentUser?.id,
    currentUser?.is_team_admin,
    currentUser?.is_team_driver,
    currentTeam?.id,
    navigate,
  ])

  useEffect(() => {
    if (!isAuthenticated && !isPublicPath) {
      return
    }
    if (isEmpty(settings)) {
      if (isAuthenticated) dispatch(getSettings())
    } else {
      if (azureMapsApiKey) {
        loadAzureMapsApis(azureMapsApiKey, () => {
          dispatch(setAzureMapsApiLoaded())
        })
      }
    }
  }, [dispatch, settings, azureMapsApiKey, isAuthenticated, isPublicPath])

  if (
    isAuthenticated &&
    !isPublicPath &&
    (!currentUser.id || (!currentTeam.id && !currentUser.vendor_membership))
  ) {
    return <Spinner open={true} />
  }

  if (isChecklistLoading) {
    return <Spinner open={true} />
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <AppUserProvider>
        <LayoutProvider>
          <OnboardingProgressProvider>
            <Layout checklist={allChecklistItems}>
              <DrawerProvider>
                <AppContainer>{children}</AppContainer>
              </DrawerProvider>
            </Layout>
          </OnboardingProgressProvider>
        </LayoutProvider>
      </AppUserProvider>
    </LocalizationProvider>
  )
}
